import { useEffect, useRef } from 'react';

/**
 * Institutional confirmation dialog. Presentational only — state and the
 * async confirm/cancel wiring live in `ConfirmProvider` / `useConfirm`.
 *
 * Adds what the base `Modal` doesn't have: dialog a11y attributes, focus
 * management, Escape-to-cancel (suppressed while loading), and an inline
 * error slot for a failed `onConfirm`.
 *
 * @param {{
 *   title: string,
 *   message: import('react').ReactNode,
 *   details?: Array<[string, import('react').ReactNode]>,
 *   confirmLabel?: string,
 *   cancelLabel?: string,
 *   tone?: 'danger' | 'primary',
 *   loading?: boolean,
 *   error?: string | null,
 *   onConfirm: () => void,
 *   onCancel: () => void,
 *   stacked?: boolean,
 * }} props
 */
export function ConfirmDialog({
    title,
    message,
    details,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'primary',
    loading = false,
    error = null,
    onConfirm,
    onCancel,
    stacked = false,
}) {
    const cancelRef = useRef(null);
    const titleId = 'confirm-dialog-title';
    const descId = 'confirm-dialog-desc';

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    useEffect(() => {
        function onKeyDown(e) {
            if (e.key === 'Escape' && !loading) {
                e.stopPropagation();
                onCancel();
            }
        }
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [loading, onCancel]);

    const submitClassName = tone === 'danger' ? 'btn-danger' : 'btn-primary';

    return (
        <div
            className={`fixed inset-0 flex items-center justify-center bg-black/40 p-4 ${stacked ? 'z-[70]' : 'z-50'}`}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !loading) onCancel();
            }}
        >
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                className="flex w-full max-w-md flex-col rounded-lg border border-border bg-card shadow-xl"
            >
                <div className="border-b border-border px-6 py-4">
                    <h3 id={titleId} className="text-lg font-semibold text-foreground">
                        {title}
                    </h3>
                </div>
                <div className="space-y-4 px-6 py-5">
                    <div id={descId} className="text-sm text-muted-foreground">
                        {message}
                    </div>
                    {details && details.length > 0 ? (
                        <dl className="space-y-1.5 rounded-md border border-border bg-muted/30 px-3 py-2.5">
                            {details.map(([label, value]) => (
                                <div key={label} className="flex items-baseline justify-between gap-3 text-xs">
                                    <dt className="font-medium uppercase tracking-wide text-muted-foreground">
                                        {label}
                                    </dt>
                                    <dd className="truncate text-right font-semibold text-foreground">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    {error ? (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                            {error}
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="btn-secondary"
                    >
                        {cancelLabel}
                    </button>
                    <button type="button" onClick={onConfirm} disabled={loading} className={submitClassName}>
                        {loading ? 'Working...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConfirmDialog;
